// Onglets de la fiche du bail.
import { useState } from "react";
import { Link } from "react-router";
import { del, patch, post, put, upload } from "@core/api";
import { date, dateTime, money, today } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorBox,
  FileDrop,
  Fil,
  Input,
  KeyValues,
  Loading,
  Modal,
  MoneyInput,
  Notice,
  Select,
  SimBadge,
  Source,
  Stat,
  StatusChip,
  Table,
  Textarea,
  fieldError,
} from "@core/ui";
import {
  CONDITIONS,
  DocLink,
  InspectionStatus,
  LEGAL_NOTICE,
  REGIMES,
  RequestStatus,
  SignerStatus,
  conditionLabel,
  newKey,
  useAction,
  type Clause,
  type Condition,
  type Doc,
  type Inspection,
  type LeaseDetail,
  type Party,
  type Prorata,
  type SignatureRequest,
} from "./shared";

function useLeaseApi(d: LeaseDetail) {
  const org = useOrg();
  const invalidate = useInvalidate();
  const base = org.api(`/leases/${d.lease.id}`);
  return { org, base, refresh: () => invalidate(org.api("/leases"), org.api("/signature-requests"), org.api("/inspections")) };
}

const SOURCE_LABELS: Record<string, string> = {
  logement: "Logement",
  regime: "Régime",
  loyer: "Loyer",
  charges: "Charges",
  depot: "Dépôt",
  prise_effet: "Prise d’effet",
  parties: "Parties",
  bailleur: "Bailleur",
  saisie: "Saisie",
};

// ---------------------------------------------------------------------------
// Bail et annexes

export function TermsTab({ d }: { d: LeaseDetail }) {
  const { org, base, refresh } = useLeaseApi(d);
  const draft = d.lease.status === "brouillon";
  const canEdit = draft && org.can("operate");
  const { busy, error, run } = useAction();
  const latest = d.versions[0];
  const steps = [
    { title: "Conditions et parties", text: "Données reprises avec leur source, à vérifier.", state: d.versions.length ? "fait" : "actuel" },
    { title: "Version PDF", text: "Chaque version est conservée et porte son empreinte.", state: latest?.matches_current_terms ? "fait" : d.versions.length ? "actuel" : "a-venir" },
    { title: "Contenu approuvé", text: "Vous validez le contenu soumis à signature.", state: d.terms.approval_valid || !draft ? "fait" : latest?.matches_current_terms ? "actuel" : "a-venir" },
    { title: "Signature", text: "Toutes les parties, dans l’ordre prévu.", state: ["signe", "actif", "preavis", "termine"].includes(d.lease.status) ? "fait" : d.lease.status === "en_signature" ? "actuel" : "a-venir" },
    { title: "Location active", text: "Espace locataire ouvert, campagne attribuée.", state: d.lease.status === "actif" ? "fait" : d.lease.status === "signe" ? "actuel" : "a-venir" },
  ] as const;
  return (
    <div className="stack">
      <Notice tone="attention">{LEGAL_NOTICE}</Notice>
      <div className="grid-2">
        <Card title="Avancement">
          <Fil steps={steps.map((s) => ({ ...s }))} />
        </Card>
        <Card title="Points à régler">
          {d.checks.problems.length === 0 ? <p>Aucun point bloquant : le contenu peut être approuvé.</p> : null}
          {d.checks.problems.length ? (
            <ul className="entree-problems">
              {d.checks.problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          ) : null}
          {d.checks.warnings.map((w) => (
            <Notice key={w} tone="attention">
              {w}
            </Notice>
          ))}
          {d.lease.status === "signe" ? (
            <div className="stack">
              <p>Le bail est signé par toutes les parties. L’activation ouvre l’espace des locataires et garants et passe la campagne en « attribuée ». La prise d’effet reste fixée au {date(d.lease.start_on)}.</p>
              {org.can("operate") ? (
                <Button busy={busy === "activate"} onClick={() => run("activate", async () => { await post(`${base}/activate`); refresh(); }, "Location activée. Les invitations ont été envoyées.")}>
                  Activer la location
                </Button>
              ) : null}
            </div>
          ) : null}
          {d.lease.activated_at ? <p className="small muted">Location activée le {dateTime(d.lease.activated_at)}.</p> : null}
          <ErrorBox error={error} />
        </Card>
      </div>
      <TermsForm d={d} canEdit={canEdit} />
      <PartiesCard d={d} />
      <ClausesCard d={d} canEdit={canEdit} />
      <AnnexesCard d={d} />
      <Card
        title="Versions du bail"
        actions={
          canEdit ? (
            <Button variant="secondaire" small busy={busy === "version"} onClick={() => run("version", async () => { await post(`${base}/versions`); refresh(); }, "Nouvelle version générée.")}>
              Générer la version PDF
            </Button>
          ) : null
        }
      >
        {d.versions.length === 0 ? <Empty title="Aucune version">Générez une version PDF pour relire le bail avant de l’approuver.</Empty> : null}
        <ul className="entree-list">
          {d.versions.map((v) => (
            <li key={v.id}>
              <div className="entree-line">
                <strong>Version {v.version}</strong>
                <span className="muted small">{dateTime(v.created_at)}</span>
              </div>
              <div className="entree-actions">
                {v.approved && d.terms.approval_valid ? <StatusChip status="conforme" label="Contenu approuvé" /> : null}
                {v.matches_current_terms ? <StatusChip status="info" label="Conforme aux termes actuels" /> : <StatusChip status="non_concluant" label="Termes modifiés depuis" />}
                <DocLink doc={v.document} label="Ouvrir le PDF" />
              </div>
              {draft && org.can("decide") && v.matches_current_terms && !(v.approved && d.terms.approval_valid) ? (
                <div>
                  <Button small busy={busy === `approve-${v.id}`} disabled={!d.checks.ready_for_approval} onClick={() => run(`approve-${v.id}`, async () => { await post(`${base}/approve`, { version_id: v.id }); refresh(); }, "Contenu approuvé.")}>
                    J’approuve ce contenu
                  </Button>
                  {!d.checks.ready_for_approval ? <span className="small muted"> Réglez d’abord les points listés plus haut.</span> : null}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
        {d.terms.approved_at ? (
          <p className="small muted">
            Approuvé le {dateTime(d.terms.approved_at)}
            {d.terms.approved_by ? ` par ${d.terms.approved_by}` : ""}.
          </p>
        ) : null}
      </Card>
    </div>
  );
}

function ProrataLine({ p }: { p: Prorata }) {
  return (
    <p className="small">
      <strong>Premier loyer : {money(p.total_cents)}</strong> pour la période du {date(p.period_start)} au {date(p.period_end)}. {p.explanation} Les appels de loyer sont émis par la gestion locative.
    </p>
  );
}

function TermsForm({ d, canEdit }: { d: LeaseDetail; canEdit: boolean }) {
  const { base, refresh } = useLeaseApi(d);
  const l = d.lease;
  const [f, setF] = useState({ regime: l.regime, start_on: l.start_on, duration_months: String(l.duration_months ?? ""), charges_mode: l.charges_mode, payment_day: String(l.payment_day) });
  const [rent, setRent] = useState<number | null>(l.rent_cents);
  const [charges, setCharges] = useState<number | null>(l.charges_cents);
  const [deposit, setDeposit] = useState<number | null>(l.deposit_cents);
  const { busy, error, run } = useAction();
  const sources = Object.entries(d.terms.prefill_sources ?? {});
  return (
    <Card title="Conditions du bail">
      {sources.length ? (
        <div className="entree-sources" aria-label="Provenance des données">
          {sources.map(([k, v]) => (
            <Source key={k} doc={SOURCE_LABELS[k] ?? k} details={[v]} />
          ))}
        </div>
      ) : null}
      {canEdit ? (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            run(
              "save",
              async () => {
                await patch(base, {
                  regime: f.regime,
                  start_on: f.start_on,
                  duration_months: Number(f.duration_months) || null,
                  rent_cents: rent ?? 0,
                  charges_cents: charges ?? 0,
                  charges_mode: f.charges_mode,
                  deposit_cents: deposit ?? 0,
                  payment_day: Number(f.payment_day) || 1,
                });
                refresh();
              },
              "Conditions enregistrées. L’approbation éventuelle a été retirée.",
            );
          }}
        >
          <ErrorBox error={error} />
          <div className="grid-2">
            <Select label="Régime" value={f.regime} options={REGIMES} error={fieldError(error, "regime")} onChange={(e) => setF({ ...f, regime: e.target.value })} />
            <Input label="Prise d’effet" type="date" value={f.start_on} error={fieldError(error, "start_on")} onChange={(e) => setF({ ...f, start_on: e.target.value })} />
            <Input label="Durée (mois)" type="number" value={f.duration_months} error={fieldError(error, "duration_months")} onChange={(e) => setF({ ...f, duration_months: e.target.value })} />
            <Input label="Jour de paiement" type="number" min={1} max={28} value={f.payment_day} error={fieldError(error, "payment_day")} onChange={(e) => setF({ ...f, payment_day: e.target.value })} />
            <MoneyInput label="Loyer mensuel hors charges" cents={rent} onChange={setRent} error={fieldError(error, "rent_cents")} />
            <MoneyInput label="Charges mensuelles" cents={charges} onChange={setCharges} error={fieldError(error, "charges_cents")} />
            <Select
              label="Nature des charges"
              value={f.charges_mode}
              options={[
                { value: "provision", label: "Provision avec régularisation" },
                { value: "forfait", label: "Forfait" },
              ]}
              onChange={(e) => setF({ ...f, charges_mode: e.target.value })}
            />
            <MoneyInput label="Dépôt de garantie" cents={deposit} onChange={setDeposit} help={d.checks.deposit_explanation} error={fieldError(error, "deposit_cents")} />
          </div>
          <div>
            <Button type="submit" variant="secondaire" busy={busy === "save"}>
              Enregistrer les conditions
            </Button>
          </div>
        </form>
      ) : (
        <KeyValues
          items={[
            ["Régime", d.regime_label],
            ["Prise d’effet", date(l.start_on)],
            ["Durée", l.duration_months ? `${l.duration_months} mois` : "—"],
            ["Loyer hors charges", <span className="entree-money">{money(l.rent_cents)}</span>],
            [l.charges_mode === "forfait" ? "Charges (forfait)" : "Provision sur charges", <span className="entree-money">{money(l.charges_cents)}</span>],
            ["Dépôt de garantie", <span className="entree-money">{money(l.deposit_cents)}</span>],
            ["Jour de paiement", `Le ${l.payment_day} du mois`],
          ]}
        />
      )}
      <p className="small muted">{d.checks.deposit_explanation}</p>
      <ProrataLine p={d.prorata} />
    </Card>
  );
}

function PartiesCard({ d }: { d: LeaseDetail }) {
  const { org, base, refresh } = useLeaseApi(d);
  const draft = d.lease.status === "brouillon";
  const signed = ["signe", "actif", "preavis"].includes(d.lease.status);
  const [adding, setAdding] = useState(false);
  const [edit, setEdit] = useState<Party | null>(null);
  const [form, setForm] = useState({ role: "locataire", full_name: "", email: "", phone: "" });
  const { busy, error, run } = useAction();
  return (
    <Card title="Parties" actions={draft && org.can("operate") ? <Button variant="secondaire" small onClick={() => { setForm({ role: "locataire", full_name: "", email: "", phone: "" }); setAdding(true); }}>Ajouter une partie</Button> : null}>
      <Table caption="Parties au bail">
        <thead>
          <tr>
            <th>Qualité</th>
            <th>Nom</th>
            <th>Coordonnées</th>
            <th>Espace personnel</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {d.parties.map((p) => (
            <tr key={p.id}>
              <td>{p.role_label}</td>
              <td>
                {p.full_name}
                {p.from_application ? <span className="block muted small">Repris du dossier retenu</span> : null}
              </td>
              <td>
                {p.email ?? <span className="muted">E-mail manquant</span>}
                {p.phone ? <span className="block small">{p.phone}</span> : null}
              </td>
              <td>
                {p.role === "locataire" || p.role === "garant" ? (
                  p.account_linked ? (
                    <StatusChip status="conforme" label="Compte rattaché" />
                  ) : p.invitation ? (
                    <StatusChip status="a_confirmer" label={`Invitation envoyée le ${date(p.invitation.sent_at)}`} />
                  ) : (
                    <StatusChip status="manquant" label="Pas encore invité" />
                  )
                ) : null}
              </td>
              <td className="right">
                <div className="entree-actions">
                  {org.can("operate") && d.lease.status !== "en_signature" ? (
                    <Button variant="discret" small onClick={() => { setEdit(p); setForm({ role: p.role, full_name: p.full_name, email: p.email ?? "", phone: p.phone ?? "" }); }}>
                      Modifier
                    </Button>
                  ) : null}
                  {draft && org.can("operate") ? (
                    <Button variant="discret" small busy={busy === `rm-${p.id}`} onClick={() => run(`rm-${p.id}`, async () => { await del(`${base}/parties/${p.id}`); refresh(); }, "Partie retirée.")}>
                      Retirer
                    </Button>
                  ) : null}
                  {signed && !p.account_linked && (p.role === "locataire" || p.role === "garant") && org.can("operate") ? (
                    <Button variant="secondaire" small busy={busy === `inv-${p.id}`} onClick={() => run(`inv-${p.id}`, async () => { await post(`${base}/parties/${p.id}/invite`); refresh(); }, "Invitation envoyée.")}>
                      Inviter à rattacher son compte
                    </Button>
                  ) : null}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Modal
        open={adding || !!edit}
        onClose={() => { setAdding(false); setEdit(null); }}
        title={edit ? `Modifier ${edit.full_name}` : "Ajouter une partie"}
        footer={
          <Button
            busy={busy === "party"}
            onClick={async () => {
              const ok = await run(
                "party",
                async () => {
                  if (edit) {
                    await patch(`${base}/parties/${edit.id}`, draft ? form : { email: form.email, phone: form.phone });
                  } else {
                    await post(`${base}/parties`, { ...form, email: form.email || null, phone: form.phone || null });
                  }
                  refresh();
                },
                "Partie enregistrée.",
              );
              if (ok) {
                setAdding(false);
                setEdit(null);
              }
            }}
          >
            Enregistrer
          </Button>
        }
      >
        <div className="stack">
          <ErrorBox error={error} />
          {!edit ? (
            <Select
              label="Qualité"
              value={form.role}
              options={[
                { value: "locataire", label: "Locataire" },
                { value: "garant", label: "Garant" },
                { value: "bailleur", label: "Bailleur" },
                { value: "mandataire", label: "Mandataire" },
              ]}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            />
          ) : null}
          <Input label="Nom et prénom" value={form.full_name} disabled={!!edit && !draft} error={fieldError(error, "full_name")} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
          <Input label="Adresse e-mail" type="email" value={form.email} error={fieldError(error, "email")} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input label="Téléphone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
      </Modal>
    </Card>
  );
}

function ClausesCard({ d, canEdit }: { d: LeaseDetail; canEdit: boolean }) {
  const { base, refresh } = useLeaseApi(d);
  const [clauses, setClauses] = useState<Clause[]>(d.terms.clauses);
  const [special, setSpecial] = useState(d.terms.special_conditions);
  const { busy, error, run } = useAction();
  if (!canEdit) {
    return (
      <Card title="Clauses">
        <ul className="entree-list">
          {d.terms.clauses.map((c) => (
            <li key={c.code}>
              <strong>{c.title}</strong>
              <span>{c.text}</span>
            </li>
          ))}
        </ul>
        {d.terms.special_conditions ? (
          <>
            <h3>Conditions particulières</h3>
            <p>{d.terms.special_conditions}</p>
          </>
        ) : null}
      </Card>
    );
  }
  return (
    <Card title="Clauses" actions={<Button variant="secondaire" small onClick={() => setClauses([...clauses, { code: `libre_${newKey().slice(0, 8)}`, title: "", text: "" }])}>Ajouter une clause</Button>}>
      <div className="stack">
        <ErrorBox error={error} />
        {clauses.map((c, i) => (
          <div key={c.code} className="entree-item">
            <Input label="Titre de la clause" value={c.title} onChange={(e) => setClauses(clauses.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
            <Textarea label="Texte" value={c.text} onChange={(e) => setClauses(clauses.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
            <div>
              <Button variant="discret" small onClick={() => setClauses(clauses.filter((_, j) => j !== i))}>
                Retirer cette clause
              </Button>
            </div>
          </div>
        ))}
        <Textarea label="Conditions particulières" value={special} help="Travaux, équipements, précisions convenues entre les parties." onChange={(e) => setSpecial(e.target.value)} />
        <div>
          <Button variant="secondaire" busy={busy === "clauses"} onClick={() => run("clauses", async () => { await patch(base, { clauses, special_conditions: special }); refresh(); }, "Clauses enregistrées. L’approbation éventuelle a été retirée.")}>
            Enregistrer les clauses
          </Button>
        </div>
      </div>
    </Card>
  );
}

function AnnexesCard({ d }: { d: LeaseDetail }) {
  const { org, base, refresh } = useLeaseApi(d);
  const draft = d.lease.status === "brouillon";
  const [na, setNa] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const { busy, error, run } = useAction();
  return (
    <Card title="Annexes">
      <p className="small muted">Liste de contrôle selon le régime. Les annexes « avant signature » sont jointes à la demande de signature ; l’état des lieux et l’inventaire sont établis à la remise des clés.</p>
      <ErrorBox error={error} />
      <ul className="entree-list">
        {d.annexes.map((a) => {
          const editable = org.can("operate") && (a.due === "remise_des_cles" || draft);
          return (
            <li key={a.code}>
              <div className="entree-line">
                <strong>{a.label}</strong>
                <span className="entree-actions">
                  {a.status === "fourni" ? (
                    <StatusChip status="conforme" label="Fournie" />
                  ) : a.status === "non_applicable" ? (
                    <StatusChip status="info" label="Non applicable" />
                  ) : a.required ? (
                    <StatusChip status="manquant" label="À fournir" />
                  ) : (
                    <StatusChip status="info" label="Facultative" />
                  )}
                  <Badge>{a.due === "signature" ? "Avant signature" : "À la remise des clés"}</Badge>
                </span>
              </div>
              <span className="small muted">{a.help}</span>
              {a.evidence ? <Source doc="Constat Bailivo" details={[a.evidence.replace(/^Constat Bailivo : /, "")]} /> : null}
              {a.note ? <span className="small">{a.note}</span> : null}
              <DocLink doc={a.document} />
              {editable ? (
                <div className="entree-actions">
                  <FileDrop
                    label={a.document ? "Remplacer le fichier" : "Déposer le fichier"}
                    busy={busy === `up-${a.code}`}
                    onFiles={(files) =>
                      run(
                        `up-${a.code}`,
                        async () => {
                          const fd = new FormData();
                          fd.append("file", files[0]);
                          await upload(`${base}/annexes/${a.code}/file`, fd);
                          refresh();
                        },
                        "Annexe enregistrée.",
                      )
                    }
                  />
                  {a.status !== "non_applicable" ? (
                    <Button variant="discret" small onClick={() => { setNa(a.code); setNote(""); }}>
                      Ne s’applique pas
                    </Button>
                  ) : (
                    <Button variant="discret" small onClick={() => run(`reset-${a.code}`, async () => { await put(`${base}/annexes/${a.code}`, { status: "a_fournir" }); refresh(); })}>
                      Remettre à fournir
                    </Button>
                  )}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      <Modal
        open={!!na}
        onClose={() => setNa(null)}
        title="Annexe non applicable"
        footer={
          <Button busy={busy === "na"} onClick={async () => { if (await run("na", async () => { await put(`${base}/annexes/${na}`, { status: "non_applicable", note }); refresh(); }, "Annexe marquée non applicable.")) setNa(null); }}>
            Enregistrer
          </Button>
        }
      >
        <Textarea label="Pourquoi cette annexe ne s’applique pas" value={note} error={fieldError(error, "note")} onChange={(e) => setNote(e.target.value)} help="Cette justification est conservée avec le bail." />
      </Modal>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Signature

type SignatureList = { items: SignatureRequest[]; provider: { nom: string; mode: string; simulateur: boolean }; quota: { utilisees: number; limite: number | null; periode: string } };

export function SignatureTab({ d }: { d: LeaseDetail }) {
  const { org, base, refresh } = useLeaseApi(d);
  const q = useGet<SignatureList>(`${base}/signature`);
  const [days, setDays] = useState("14");
  const { busy, error, run } = useAction();
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorBox error={q.error} />;
  const s = q.data;
  const bail = s.items.filter((r) => r.subject === "bail");
  const active = bail.find((r) => r.status === "en_cours" || r.status === "a_recuperer");
  const past = bail.filter((r) => r !== active);
  return (
    <div className="stack">
      <Card title={<>Signature électronique <SimBadge mode={s.provider.mode} /></>}>
        {s.provider.simulateur ? (
          <Notice tone="simulateur" title="Mode simulateur">
            Aucun document réel n’est signé : les liens, les événements et le dossier de preuve sont produits par le simulateur Youtrust de Bailivo. L’intégration réelle attend un compte et un contrat (INT-01).
          </Notice>
        ) : null}
        <p className="small muted">
          Signatures décomptées en {s.quota.periode} : {s.quota.utilisees}
          {s.quota.limite !== null ? ` sur ${s.quota.limite}` : ""}. Une nouvelle demande pour le même bail la même année n’est pas décomptée deux fois.
        </p>
        <ErrorBox error={error} />
        {!active && d.lease.status === "brouillon" ? (
          d.terms.approval_valid ? (
            org.can("decide") ? (
              <div className="entree-grid-form">
                <Select
                  label="Délai de signature"
                  value={days}
                  options={[
                    { value: "7", label: "7 jours" },
                    { value: "14", label: "14 jours" },
                    { value: "30", label: "30 jours" },
                  ]}
                  onChange={(e) => setDays(e.target.value)}
                />
                <Button busy={busy === "start"} onClick={() => run("start", async () => { await post(`${base}/signature`, { expires_in_days: Number(days) }); refresh(); }, "Demande de signature envoyée au premier signataire.")}>
                  Envoyer en signature
                </Button>
              </div>
            ) : (
              <p>Votre rôle ne permet pas d’envoyer le bail en signature.</p>
            )
          ) : (
            <Notice>Approuvez une version du bail (onglet « Bail et annexes ») avant l’envoi en signature.</Notice>
          )
        ) : null}
      </Card>
      {active ? <RequestCard r={active} simulator={s.provider.simulateur} /> : null}
      {past.length ? (
        <Card title="Demandes précédentes">
          <div className="stack">
            {past.map((r) => (
              <RequestCard key={r.id} r={r} simulator={s.provider.simulateur} compact />
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
}

export function RequestCard({ r, simulator, compact }: { r: SignatureRequest; simulator: boolean; compact?: boolean }) {
  const org = useOrg();
  const invalidate = useInvalidate();
  const rbase = org.api(`/signature-requests/${r.id}`);
  const refresh = () => invalidate(org.api("/leases"), org.api("/signature-requests"), org.api("/inspections"));
  const { busy, error, run } = useAction();
  const [modal, setModal] = useState<{ kind: "refus" | "adresse" | "annulation"; signerId?: string } | null>(null);
  const [text, setText] = useState("");
  const open = r.status === "en_cours";
  const content = (
    <div className="stack">
      <div className="entree-line">
        <span>
          <RequestStatus r={r} /> {r.version ? <span className="muted small">Version {r.version} du bail</span> : null}
        </span>
        <span className="muted small">
          Créée le {dateTime(r.created_at)} · {open ? `expire le ${dateTime(r.expires_at)}` : r.completed_at ? `close le ${dateTime(r.completed_at)}` : ""}
        </span>
      </div>
      {r.refusal_reason ? <Notice tone="erreur" title="Motif du refus">{r.refusal_reason}</Notice> : null}
      {r.cancel_reason ? <p className="small">Motif d’annulation : {r.cancel_reason}</p> : null}
      {r.status === "a_recuperer" ? (
        <Notice tone="attention" title="Signée chez le prestataire, originaux non récupérés">
          {r.retrieval_error} Le bail n’est pas présenté comme signé tant que les originaux ne sont pas conservés.
        </Notice>
      ) : null}
      <ErrorBox error={error} />
      <Table caption="Signataires">
        <thead>
          <tr>
            <th>Ordre</th>
            <th>Signataire</th>
            <th>Statut</th>
            <th>Relances</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {r.signers.map((s) => (
            <tr key={s.id}>
              <td>{s.position}</td>
              <td>
                {s.full_name} <span className="muted small">· {s.role_label}</span>
                <span className="block small">{s.email}</span>
              </td>
              <td>
                <SignerStatus s={s} />
                {s.signed_at ? <span className="block small muted">le {dateTime(s.signed_at)}</span> : null}
                {s.refusal_reason ? <span className="block small">Motif : {s.refusal_reason}</span> : null}
              </td>
              <td>
                {s.reminder_count}
                {s.last_reminder_at ? <span className="block small muted">dernière le {dateTime(s.last_reminder_at)}</span> : null}
              </td>
              <td className="right">
                {open && s.status === "en_attente" && org.can("operate") ? (
                  <div className="entree-actions">
                    {s.is_turn ? (
                      <Button variant="secondaire" small busy={busy === `rem-${s.id}`} onClick={() => run(`rem-${s.id}`, async () => { await post(`${rbase}/signers/${s.id}/remind`); refresh(); }, "Relance envoyée.")}>
                        Relancer
                      </Button>
                    ) : null}
                    <Button variant="discret" small onClick={() => { setText(s.email ?? ""); setModal({ kind: "adresse", signerId: s.id }); }}>
                      Corriger l’adresse
                    </Button>
                    {simulator && s.is_turn ? (
                      <>
                        <Button variant="secondaire" small busy={busy === `sim-${s.id}`} onClick={() => run(`sim-${s.id}`, async () => { await post(`${rbase}/simulate`, { action: "signer", signer_id: s.id }); refresh(); }, "Signature simulée.")}>
                          Simuler la signature
                        </Button>
                        <Button variant="discret" small onClick={() => { setText(""); setModal({ kind: "refus", signerId: s.id }); }}>
                          Simuler un refus
                        </Button>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {r.signed_documents.length || r.proof ? (
        <div className="stack-sm">
          <strong>Originaux conservés</strong>
          {r.signed_documents.map((doc: Doc) => (doc ? <DocLink key={doc.id} doc={doc} /> : null))}
          {r.proof ? <DocLink doc={r.proof} label="Dossier de preuve" /> : null}
          <span className="small muted">Les originaux ne sont jamais modifiés ; l’empreinte permet de vérifier leur intégrité.</span>
        </div>
      ) : null}
      {(open || r.status === "a_recuperer") && org.can("operate") ? (
        <div className="entree-actions">
          <Button variant="secondaire" small busy={busy === "sync"} onClick={() => run("sync", async () => { await post(`${rbase}/sync`); refresh(); }, "Statut resynchronisé avec le prestataire.")}>
            {r.status === "a_recuperer" ? "Réessayer la récupération" : "Resynchroniser"}
          </Button>
          {open && org.can("decide") ? (
            <Button variant="danger" small onClick={() => { setText(""); setModal({ kind: "annulation" }); }}>
              Annuler la demande
            </Button>
          ) : null}
          {simulator && open ? (
            <>
              <Button variant="discret" small busy={busy === "exp"} onClick={() => run("exp", async () => { await post(`${rbase}/simulate`, { action: "expirer" }); refresh(); }, "Expiration simulée.")}>
                Simuler l’expiration
              </Button>
              <Button variant="discret" small busy={busy === "panne"} onClick={() => run("panne", async () => { await post(`${rbase}/simulate`, { action: "panne_telechargement", count: 1 }); refresh(); }, "La prochaine récupération des originaux échouera.")}>
                Simuler une panne du prestataire
              </Button>
            </>
          ) : null}
        </div>
      ) : null}
      <Modal
        open={!!modal}
        onClose={() => setModal(null)}
        title={modal?.kind === "refus" ? "Simuler un refus" : modal?.kind === "adresse" ? "Corriger l’adresse du signataire" : "Annuler la demande de signature"}
        footer={
          <Button
            busy={busy === "modal"}
            variant={modal?.kind === "annulation" ? "danger" : "primaire"}
            onClick={async () => {
              const m = modal;
              if (!m) return;
              const ok = await run("modal", async () => {
                if (m.kind === "refus") await post(`${rbase}/simulate`, { action: "refuser", signer_id: m.signerId, reason: text });
                if (m.kind === "adresse") await patch(`${rbase}/signers/${m.signerId}`, { email: text });
                if (m.kind === "annulation") await post(`${rbase}/cancel`, { reason: text });
                refresh();
              });
              if (ok) setModal(null);
            }}
          >
            Confirmer
          </Button>
        }
      >
        {modal?.kind === "adresse" ? (
          <Input label="Nouvelle adresse e-mail" type="email" value={text} error={fieldError(error, "email")} onChange={(e) => setText(e.target.value)} help="Un nouveau lien est envoyé si c’est le tour de ce signataire." />
        ) : (
          <Textarea label={modal?.kind === "refus" ? "Motif du refus (simulation)" : "Motif de l’annulation"} value={text} error={fieldError(error, "reason")} onChange={(e) => setText(e.target.value)} />
        )}
      </Modal>
    </div>
  );
  if (compact) return <div className="entree-item">{content}</div>;
  return <Card title={r.subject === "bail" ? "Demande en cours" : "Signature de l’état des lieux"}>{content}</Card>;
}

// ---------------------------------------------------------------------------
// États des lieux, inventaire, comparaison

export function InspectionsTab({ d }: { d: LeaseDetail }) {
  const { org, base, refresh } = useLeaseApi(d);
  const q = useGet<{ items: Inspection[]; furnished: boolean }>(`${base}/inspections`);
  const [when, setWhen] = useState("");
  const { busy, error, run } = useAction();
  const items = q.data?.items ?? [];
  const hasEntry = items.some((i) => i.kind === "entree");
  const hasExit = items.some((i) => i.kind === "sortie");
  const create = (kind: "entree" | "sortie") =>
    run(`new-${kind}`, async () => { await post(`${base}/inspections`, { kind, scheduled_at: when ? new Date(when).toISOString() : null }); refresh(); }, "État des lieux créé.");
  return (
    <div className="stack">
      <Card title="États des lieux">
        <p className="small muted">Même outil pour l’entrée et la sortie. La saisie fonctionne sur mobile, y compris sans réseau : les données sont gardées sur l’appareil puis envoyées.</p>
        {q.isLoading ? <Loading /> : null}
        <ErrorBox error={q.error ?? error} />
        <ul className="entree-list">
          {items.map((i) => (
            <li key={i.id}>
              <div className="entree-line">
                <Link to={org.to(`baux/${d.lease.id}/etats-des-lieux/${i.id}`)}>
                  <strong>
                    {i.kind_label} · version {i.version}
                  </strong>
                </Link>
                <InspectionStatus i={i} />
              </div>
              <span className="small muted">
                {i.performed_at ? `Réalisé le ${dateTime(i.performed_at)}` : i.scheduled_at ? `Prévu le ${dateTime(i.scheduled_at)}` : "Date à fixer"} · {i.stats.rooms} pièce(s), {i.stats.items} élément(s), {i.stats.photos} photo(s)
                {i.stats.items_without_condition ? ` · ${i.stats.items_without_condition} sans état` : ""}
                {i.stats.items_without_photo ? ` · ${i.stats.items_without_photo} sans photo` : ""}
                {i.superseded_by ? " · remplacé par une version plus récente" : ""}
              </span>
              {i.pdf ? <DocLink doc={i.pdf} label="PDF validé" /> : null}
            </li>
          ))}
        </ul>
        {org.can("operate") && (!hasEntry || !hasExit) ? (
          <div className="entree-grid-form">
            <Input label="Date prévue (facultatif)" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
            {!hasEntry ? (
              <Button busy={busy === "new-entree"} onClick={() => create("entree")}>
                Préparer l’état des lieux d’entrée
              </Button>
            ) : (
              <Button variant="secondaire" busy={busy === "new-sortie"} onClick={() => create("sortie")}>
                Préparer l’état des lieux de sortie
              </Button>
            )}
          </div>
        ) : null}
      </Card>
      {q.data?.furnished ? <InventoryCard d={d} /> : null}
      {hasEntry && hasExit ? <CompareCard d={d} /> : null}
    </div>
  );
}

type InvItem = {
  key: string;
  name: string;
  category: string | null;
  room: string;
  quantity: number;
  condition: Condition | null;
  owner: string;
  status: string;
  replaced_by: string | null;
  notes: string;
};
type InventoryData = {
  furnished: boolean;
  inventory: { id: string; version: number; status: string; locked_at: string | null; based_on_id: string | null } | null;
  items: InvItem[];
  minimal_check: { code: string; label: string; present: boolean; items: string[] }[];
  minimal_list: { code: string; label: string }[];
  notice: string;
  previous: { id: string; version: number; updated_at: string } | null;
};

function InventoryCard({ d }: { d: LeaseDetail }) {
  const { org, base, refresh } = useLeaseApi(d);
  const q = useGet<InventoryData>(`${base}/inventory`);
  const [items, setItems] = useState<InvItem[] | null>(null);
  const { busy, error, run } = useAction();
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorBox error={q.error} />;
  const inv = q.data;
  const locked = inv.inventory?.status === "signe";
  const editable = !locked && org.can("operate");
  const rows = items ?? inv.items;
  const set = (i: number, patch: Partial<InvItem>) => setItems(rows.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const categoryOptions = [{ value: "", label: "Autre équipement" }, ...inv.minimal_list.map((m) => ({ value: m.code, label: m.label }))];
  return (
    <Card title={`Inventaire du meublé${inv.inventory ? ` · version ${inv.inventory.version}` : ""}`}>
      <p className="small muted">{inv.notice}</p>
      <ul className="entree-list" aria-label="Liste minimale d’équipements">
        {inv.minimal_check.map((c) => (
          <li key={c.code} className="entree-line">
            <span>{c.label}</span>
            {c.present ? <StatusChip status="conforme" label={`Présent : ${c.items.join(", ")}`} /> : <StatusChip status="manquant" label="Absent de l’inventaire" />}
          </li>
        ))}
      </ul>
      {locked ? <p className="small">Inventaire signé le {dateTime(inv.inventory?.locked_at)} : il ne se modifie plus. Une nouvelle version conserve celle-ci intacte.</p> : null}
      <ErrorBox error={error} />
      {editable ? (
        <div className="stack">
          {rows.map((it, i) => (
            <div key={it.key} className="entree-item">
              <div className="entree-grid-form">
                <Input label="Équipement" value={it.name} onChange={(e) => set(i, { name: e.target.value })} />
                <Select label="Catégorie" value={it.category ?? ""} options={categoryOptions} onChange={(e) => set(i, { category: e.target.value || null })} />
                <Input label="Pièce" value={it.room} onChange={(e) => set(i, { room: e.target.value })} />
                <Input label="Quantité" type="number" min={0} value={String(it.quantity)} onChange={(e) => set(i, { quantity: Number(e.target.value) || 0 })} />
                <Select label="État" value={it.condition ?? ""} options={[{ value: "", label: "Non renseigné" }, ...CONDITIONS.map((c) => ({ value: c.value, label: c.label }))]} onChange={(e) => set(i, { condition: (e.target.value || null) as Condition | null })} />
                <Select
                  label="Appartient"
                  value={it.owner}
                  options={[
                    { value: "bailleur", label: "Au bailleur (fourni)" },
                    { value: "locataire", label: "Au locataire" },
                  ]}
                  onChange={(e) => set(i, { owner: e.target.value })}
                />
                <Select
                  label="Présence"
                  value={it.status}
                  options={[
                    { value: "present", label: "Présent" },
                    { value: "absent", label: "Absent" },
                    { value: "remplace", label: "Remplacé (historique conservé)" },
                  ]}
                  onChange={(e) => set(i, { status: e.target.value })}
                />
              </div>
              <div>
                <Button variant="discret" small onClick={() => setItems(rows.filter((_, j) => j !== i))}>
                  Retirer la ligne
                </Button>
              </div>
            </div>
          ))}
          <div className="entree-actions">
            <Button variant="secondaire" small onClick={() => setItems([...rows, { key: newKey(), name: "", category: null, room: "", quantity: 1, condition: null, owner: "bailleur", status: "present", replaced_by: null, notes: "" }])}>
              Ajouter un équipement
            </Button>
            <Button small busy={busy === "save"} onClick={() => run("save", async () => { await put(`${base}/inventory`, { items: rows, inventory_id: inv.inventory?.id ?? null }); setItems(null); refresh(); }, "Inventaire enregistré.")}>
              Enregistrer l’inventaire
            </Button>
            {inv.inventory && rows.length ? (
              <Button variant="discret" small busy={busy === "lock"} onClick={() => run("lock", async () => { await post(`${base}/inventory/lock`); refresh(); }, "Inventaire signé.")}>
                Marquer l’inventaire comme signé
              </Button>
            ) : null}
            {!inv.inventory && inv.previous ? (
              <Button variant="discret" small busy={busy === "prev"} onClick={() => run("prev", async () => { await post(`${base}/inventory/new-version`, { from_inventory_id: inv.previous?.id }); refresh(); }, "Inventaire de la location précédente repris.")}>
                Reprendre l’inventaire de la location précédente
              </Button>
            ) : null}
          </div>
          <span className="small muted">La validation de l’état des lieux d’entrée signe aussi l’inventaire en cours.</span>
        </div>
      ) : (
        <>
          <Table caption="Inventaire">
            <thead>
              <tr>
                <th>Équipement</th>
                <th>Pièce</th>
                <th className="right">Qté</th>
                <th>État</th>
                <th>Appartient</th>
              </tr>
            </thead>
            <tbody>
              {inv.items.map((it) => (
                <tr key={it.key}>
                  <td>
                    {it.name}
                    {it.status !== "present" ? <span className="block small muted">{it.status === "remplace" ? "Remplacé" : "Absent"}</span> : null}
                  </td>
                  <td>{it.room}</td>
                  <td className="right">{it.quantity}</td>
                  <td>{conditionLabel(it.condition)}</td>
                  <td>{it.owner === "bailleur" ? "Bailleur" : "Locataire"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {locked && org.can("operate") ? (
            <Button variant="secondaire" small busy={busy === "nv"} onClick={() => run("nv", async () => { await post(`${base}/inventory/new-version`, {}); refresh(); }, "Nouvelle version créée.")}>
              Créer une nouvelle version
            </Button>
          ) : null}
        </>
      )}
    </Card>
  );
}

type CompareItem = {
  name: string;
  entree_key: string | null;
  sortie_key: string | null;
  entree: Condition | null;
  sortie: Condition | null;
  entree_comment: string;
  sortie_comment: string;
  change: "inchange" | "degrade" | "ameliore" | "non_renseigne" | "absent_sortie" | "nouveau_sortie";
  change_label: string;
  wear: { category: string; wear_pct: number; explanation: string } | null;
};
type CompareData = {
  comparison: {
    rooms: { name: string; entree_key: string | null; sortie_key: string | null; items: CompareItem[] }[];
    meters: { label: string; entree: string; sortie: string; consumption: string | null }[];
    keys: { label: string; remis: number; restitues: number; ecart: number }[];
    months_occupied: number;
    summary: { inchange: number; a_examiner: number; ameliore: number; non_renseigne: number; absent_sortie: number; nouveau_sortie: number };
    notice: string;
  };
  entree: { id: string; version: number; status_label: string; performed_at: string | null };
  sortie: { id: string; version: number; status_label: string; performed_at: string | null };
  photos: { entree: { room_key: string; item_key: string | null; url: string }[]; sortie: { room_key: string; item_key: string | null; url: string }[] };
};

function ChangeChip({ c }: { c: CompareItem }) {
  const map: Record<CompareItem["change"], "conforme" | "a_confirmer" | "info" | "manquant"> = {
    inchange: "conforme",
    degrade: "a_confirmer",
    ameliore: "info",
    non_renseigne: "manquant",
    absent_sortie: "a_confirmer",
    nouveau_sortie: "info",
  };
  return <StatusChip status={map[c.change]} label={c.change_label} />;
}

function PhotoLinks({ list, room, item }: { list: { room_key: string; item_key: string | null; url: string }[]; room: string | null; item: string | null }) {
  const ps = list.filter((p) => p.room_key === room && p.item_key === item);
  if (!ps.length) return <span className="muted small">Aucune photo</span>;
  return (
    <span className="small">
      {ps.map((p, i) => (
        <a key={p.url} href={p.url} target="_blank" rel="noreferrer">
          Photo {i + 1}{" "}
        </a>
      ))}
    </span>
  );
}

function CompareCard({ d }: { d: LeaseDetail }) {
  const { org, base } = useLeaseApi(d);
  const q = useGet<CompareData>(`${base}/inspections/compare`);
  const [pdf, setPdf] = useState<Doc>(null);
  const { busy, error, run } = useAction();
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorBox error={q.error} />;
  const c = q.data.comparison;
  return (
    <Card title="Comparaison entrée / sortie">
      <Notice tone="attention">{c.notice}</Notice>
      <div className="stats">
        <Stat label="Écarts à examiner" value={c.summary.a_examiner} />
        <Stat label="Inchangés" value={c.summary.inchange} />
        <Stat label="Non renseignés" value={c.summary.non_renseigne} />
        <Stat label="Durée d’occupation" value={`${c.months_occupied} mois`} />
      </div>
      <p className="small muted">
        Entrée version {q.data.entree.version} ({q.data.entree.status_label}) · Sortie version {q.data.sortie.version} ({q.data.sortie.status_label})
      </p>
      {c.rooms.map((r) => (
        <section key={`${r.entree_key}-${r.sortie_key}`} className="stack-sm">
          <h3>{r.name}</h3>
          <div className="entree-compare">
            <span className="entree-compare-head">Élément</span>
            <span className="entree-compare-head">Constat</span>
            <span className="entree-compare-head">Entrée</span>
            <span className="entree-compare-head">Sortie</span>
            {r.items.map((i) => (
              <div key={`${i.entree_key}-${i.sortie_key}`} style={{ display: "contents" }}>
                <span>
                  <strong>{i.name}</strong>
                  {i.wear ? <span className="block entree-wear">{i.wear.explanation}</span> : null}
                </span>
                <span>
                  <ChangeChip c={i} />
                </span>
                <span>
                  {conditionLabel(i.entree)}
                  {i.entree_comment ? <span className="block small muted">{i.entree_comment}</span> : null}
                  <span className="block">
                    <PhotoLinks list={q.data!.photos.entree} room={r.entree_key} item={i.entree_key} />
                  </span>
                </span>
                <span>
                  {conditionLabel(i.sortie)}
                  {i.sortie_comment ? <span className="block small muted">{i.sortie_comment}</span> : null}
                  <span className="block">
                    <PhotoLinks list={q.data!.photos.sortie} room={r.sortie_key} item={i.sortie_key} />
                  </span>
                </span>
              </div>
            ))}
          </div>
        </section>
      ))}
      {c.meters.length ? (
        <Table caption="Compteurs">
          <thead>
            <tr>
              <th>Compteur</th>
              <th className="right">Entrée</th>
              <th className="right">Sortie</th>
              <th className="right">Consommation</th>
            </tr>
          </thead>
          <tbody>
            {c.meters.map((m) => (
              <tr key={m.label}>
                <td>{m.label}</td>
                <td className="right">{m.entree || "—"}</td>
                <td className="right">{m.sortie || "—"}</td>
                <td className="right">{m.consumption ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
      {c.keys.length ? (
        <Table caption="Clés">
          <thead>
            <tr>
              <th>Clés</th>
              <th className="right">Remises</th>
              <th className="right">Restituées</th>
              <th className="right">Écart</th>
            </tr>
          </thead>
          <tbody>
            {c.keys.map((k) => (
              <tr key={k.label}>
                <td>{k.label}</td>
                <td className="right">{k.remis}</td>
                <td className="right">{k.restitues}</td>
                <td className="right">{k.ecart}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
      <ErrorBox error={error} />
      <div className="entree-actions">
        {org.can("operate") ? (
          <Button variant="secondaire" small busy={busy === "pdf"} onClick={() => run("pdf", async () => { const r = await post<{ document: Doc }>(`${base}/inspections/compare/pdf`, { entree_id: q.data!.entree.id, sortie_id: q.data!.sortie.id }); setPdf(r.document); }, "Comparaison exportée.")}>
            Exporter en PDF
          </Button>
        ) : null}
        {pdf ? <DocLink doc={pdf} label="Comparaison (PDF)" /> : null}
      </div>
      <p className="small muted">Le décompte du dépôt de garantie s’établit dans la vie du bail, à partir des écarts que vous retenez et des justificatifs.</p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Dépôt de garantie et premier règlement

type Receipt = { id: string; amount_cents: number; status: string; received_on: string; method: string; payer: string; reference: string; note: string; cancelled_at: string | null; cancel_reason: string | null };
type DepositData = {
  expected_cents: number;
  received_cents: number;
  announced_cents: number;
  remaining_cents: number;
  status: "sans_depot" | "attendu" | "partiel" | "recu";
  cap_cents: number | null;
  cap_explanation: string;
  cap_problem: string | null;
  receipts: Receipt[];
  prorata: Prorata;
  first_rent_call: { period_start: string; period_end: string; total_cents: number; status: string } | null;
  notice: string;
};

const METHODS = [
  { value: "virement", label: "Virement" },
  { value: "cheque", label: "Chèque" },
  { value: "especes", label: "Espèces" },
  { value: "autre", label: "Autre" },
];

export function DepositTab({ d }: { d: LeaseDetail }) {
  const { org, base, refresh } = useLeaseApi(d);
  const q = useGet<DepositData>(`${base}/deposit`);
  const [amount, setAmount] = useState<number | null>(null);
  const [f, setF] = useState({ received_on: today(), method: "virement", status: "recu", payer: "", reference: "" });
  const [cancel, setCancel] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [formKey, setFormKey] = useState(0);
  const { busy, error, run } = useAction();
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorBox error={q.error} />;
  const dep = q.data;
  const statusChip =
    dep.status === "recu" ? <StatusChip status="conforme" label="Dépôt reçu" /> : dep.status === "partiel" ? <StatusChip status="a_confirmer" label="Dépôt reçu en partie" /> : dep.status === "sans_depot" ? <StatusChip status="info" label="Aucun dépôt prévu" /> : <StatusChip status="manquant" label="Dépôt attendu" />;
  return (
    <div className="stack">
      <Card title="Dépôt de garantie" actions={statusChip}>
        <div className="stats">
          <Stat label="Prévu au bail" value={money(dep.expected_cents)} />
          <Stat label="Reçu" value={money(dep.received_cents)} />
          <Stat label="Annoncé, non constaté" value={money(dep.announced_cents)} />
          <Stat label="Reste à recevoir" value={money(dep.remaining_cents)} />
        </div>
        <p className="small muted">{dep.cap_explanation}</p>
        {dep.cap_problem ? <Notice tone="erreur">{dep.cap_problem}</Notice> : null}
        <Notice>{dep.notice}</Notice>
        <ErrorBox error={error} />
        {dep.receipts.length ? (
          <Table caption="Versements du dépôt">
            <thead>
              <tr>
                <th>Date</th>
                <th className="right">Montant</th>
                <th>Moyen</th>
                <th>État</th>
                <th>Payeur, référence</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {dep.receipts.map((r) => (
                <tr key={r.id}>
                  <td>{date(r.received_on)}</td>
                  <td className="right entree-money">{money(r.amount_cents)}</td>
                  <td>{METHODS.find((m) => m.value === r.method)?.label}</td>
                  <td>
                    {r.cancelled_at ? (
                      <StatusChip status="non_concluant" label="Annulé" />
                    ) : r.status === "recu" ? (
                      <StatusChip status="conforme" label="Reçu" />
                    ) : (
                      <StatusChip status="a_confirmer" label="Annoncé" />
                    )}
                    {r.cancel_reason ? <span className="block small">{r.cancel_reason}</span> : null}
                  </td>
                  <td>
                    {r.payer}
                    {r.reference ? <span className="block small muted">{r.reference}</span> : null}
                  </td>
                  <td className="right">
                    {!r.cancelled_at && org.can("finance") ? (
                      <div className="entree-actions">
                        {r.status === "annonce" ? (
                          <Button variant="secondaire" small busy={busy === `c-${r.id}`} onClick={() => run(`c-${r.id}`, async () => { await post(`${base}/deposit/receipts/${r.id}/confirm`, { received_on: today() }); refresh(); }, "Réception confirmée.")}>
                            Confirmer la réception
                          </Button>
                        ) : null}
                        <Button variant="discret" small onClick={() => { setCancel(r.id); setReason(""); }}>
                          Annuler
                        </Button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
        {org.can("finance") && dep.expected_cents > 0 && dep.remaining_cents > 0 ? (
          <form
            key={formKey}
            className="entree-grid-form"
            onSubmit={async (e) => {
              e.preventDefault();
              const ok = await run("add", async () => { await post(`${base}/deposit/receipts`, { ...f, amount_cents: amount ?? 0 }); refresh(); }, "Versement enregistré.");
              if (ok) {
                setAmount(null);
                setFormKey(formKey + 1);
              }
            }}
          >
            <MoneyInput label="Montant" cents={amount} onChange={setAmount} error={fieldError(error, "amount_cents")} />
            <Input label="Date" type="date" value={f.received_on} error={fieldError(error, "received_on")} onChange={(e) => setF({ ...f, received_on: e.target.value })} />
            <Select label="Moyen" value={f.method} options={METHODS} onChange={(e) => setF({ ...f, method: e.target.value })} />
            <Select
              label="État"
              value={f.status}
              options={[
                { value: "recu", label: "Reçu (constaté)" },
                { value: "annonce", label: "Annoncé (promesse de virement)" },
              ]}
              onChange={(e) => setF({ ...f, status: e.target.value })}
            />
            <Input label="Payeur" value={f.payer} onChange={(e) => setF({ ...f, payer: e.target.value })} />
            <Input label="Référence" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} />
            <Button type="submit" busy={busy === "add"}>
              Enregistrer le versement
            </Button>
          </form>
        ) : null}
      </Card>
      <Card title="Premier loyer">
        <ProrataLine p={dep.prorata} />
        {dep.first_rent_call ? (
          <p>
            Premier appel de loyer : {money(dep.first_rent_call.total_cents)} du {date(dep.first_rent_call.period_start)} au {date(dep.first_rent_call.period_end)} ·{" "}
            {dep.first_rent_call.status === "paye" ? <StatusChip status="conforme" label="Payé" /> : <StatusChip status="a_confirmer" label="Non soldé" />}
          </p>
        ) : (
          <p className="small muted">Aucun appel de loyer n’a encore été émis par la gestion locative.</p>
        )}
      </Card>
      <Modal
        open={!!cancel}
        onClose={() => setCancel(null)}
        title="Annuler le versement"
        footer={
          <Button variant="danger" busy={busy === "cancel"} onClick={async () => { if (await run("cancel", async () => { await post(`${base}/deposit/receipts/${cancel}/cancel`, { reason }); refresh(); }, "Versement annulé.")) setCancel(null); }}>
            Annuler le versement
          </Button>
        }
      >
        <Textarea label="Motif de l’annulation" value={reason} error={fieldError(error, "reason")} onChange={(e) => setReason(e.target.value)} help="L’annulation reste visible dans l’historique." />
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Livret du logement

type Section = { theme: string; title: string; body: string };
type HandbookData = { sections: Section[]; version: number | null; updated_at: string | null; updated_by: string | null; themes: { code: string; label: string }[]; sensitive_sections: number[] };

export function HandbookTab({ d }: { d: LeaseDetail }) {
  const { org, base, refresh } = useLeaseApi(d);
  const q = useGet<HandbookData>(`${base}/handbook`);
  const [sections, setSections] = useState<Section[] | null>(null);
  const [theme, setTheme] = useState("equipements");
  const { busy, error, run } = useAction();
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorBox error={q.error} />;
  const h = q.data;
  const rows = sections ?? h.sections;
  const editable = org.can("operate");
  const set = (i: number, patch: Partial<Section>) => setSections(rows.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  return (
    <Card
      title={`Livret du logement${h.version ? ` · version ${h.version}` : ""}`}
      actions={h.version ? <a href={`${base}/handbook/pdf`} target="_blank" rel="noreferrer">Exporter en PDF</a> : null}
    >
      <p className="small muted">Informations pratiques partagées avec les locataires du logement ; elles ne modifient pas le bail. Évitez d’y inscrire des codes d’accès.</p>
      {h.updated_at ? <p className="small muted">Mis à jour le {dateTime(h.updated_at)}{h.updated_by ? ` par ${h.updated_by}` : ""}.</p> : null}
      {h.sensitive_sections.length ? <Notice tone="attention">Une rubrique semble contenir un code d’accès : vérifiez qu’il doit être partagé avec les locataires.</Notice> : null}
      <ErrorBox error={error} />
      {rows.length === 0 ? <Empty title="Livret vide">Ajoutez les consignes utiles : équipements, tri des déchets, compteurs, contacts.</Empty> : null}
      <div className="stack">
        {rows.map((s, i) => (
          <div key={i} className="entree-item">
            <div className="entree-line">
              <strong>{h.themes.find((t) => t.code === s.theme)?.label}</strong>
              {editable ? (
                <Button variant="discret" small onClick={() => setSections(rows.filter((_, j) => j !== i))}>
                  Retirer
                </Button>
              ) : null}
            </div>
            {editable ? (
              <>
                <Input label="Titre" value={s.title} onChange={(e) => set(i, { title: e.target.value })} />
                <Textarea label="Contenu" value={s.body} onChange={(e) => set(i, { body: e.target.value })} />
              </>
            ) : (
              <>
                {s.title ? <strong>{s.title}</strong> : null}
                <p style={{ whiteSpace: "pre-line" }}>{s.body}</p>
              </>
            )}
          </div>
        ))}
        {editable ? (
          <div className="entree-grid-form">
            <Select label="Thème" value={theme} options={h.themes.map((t) => ({ value: t.code, label: t.label }))} onChange={(e) => setTheme(e.target.value)} />
            <Button variant="secondaire" onClick={() => setSections([...rows, { theme, title: "", body: "" }])}>
              Ajouter une rubrique
            </Button>
            <Button busy={busy === "save"} onClick={() => run("save", async () => { await put(`${base}/handbook`, { sections: rows, base_version: h.version }); setSections(null); refresh(); }, "Livret enregistré : les locataires voient cette version.")}>
              Enregistrer le livret
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Remise des clés

type Step = { code: string; label: string; responsible: string; status: string; note: string; document: Doc; confirmed_at: string | null; confirmed_by: string | null; evidence: string | null };
type HandoverData = {
  steps: Step[];
  keys: { label: string; count: number }[];
  keys_from_inspection: { label: string; count: number }[];
  handed_over_on: string | null;
  attestation: Doc;
  landlord_confirmed_at: string | null;
  landlord_confirmed_by: string | null;
  tenant_confirmed_at: string | null;
  tenant_confirmed_by: string | null;
  notice: string;
};

const STEP_STATUSES = [
  { value: "a_faire", label: "À faire" },
  { value: "fait", label: "Confirmé" },
  { value: "reserve", label: "Réserve" },
  { value: "non_applicable", label: "Non applicable" },
];

export function HandoverTab({ d }: { d: LeaseDetail }) {
  const { org, base, refresh } = useLeaseApi(d);
  const ready = ["signe", "actif", "preavis"].includes(d.lease.status);
  const q = useGet<HandoverData>(ready ? `${base}/handover` : null);
  const [steps, setSteps] = useState<Record<string, { status: string; note: string; document_id?: string }>>({});
  const [keys, setKeys] = useState<{ label: string; count: number }[] | null>(null);
  const [day, setDay] = useState<string | null>(null);
  const { busy, error, run } = useAction();
  if (!ready) return <Notice>La remise des clés se prépare une fois le bail signé par toutes les parties.</Notice>;
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorBox error={q.error} />;
  const h = q.data;
  const keyRows = keys ?? h.keys;
  const editable = org.can("operate");
  const stepValue = (s: Step) => steps[s.code] ?? { status: s.status, note: s.note };
  return (
    <div className="stack">
      <Card title="Liste de contrôle de l’entrée">
        <Notice>{h.notice}</Notice>
        <ErrorBox error={error} />
        <ul className="entree-list">
          {h.steps.map((s) => {
            const v = stepValue(s);
            return (
              <li key={s.code}>
                <div className="entree-line">
                  <strong>{s.label}</strong>
                  <span className="entree-actions">
                    {s.status === "fait" ? <StatusChip status="conforme" label="Confirmé" /> : s.status === "reserve" ? <StatusChip status="a_confirmer" label="Réserve" /> : s.status === "non_applicable" ? <StatusChip status="info" label="Non applicable" /> : <StatusChip status="manquant" label="À faire" />}
                    <Badge>{s.responsible === "locataire" ? "Côté locataire" : "Côté bailleur"}</Badge>
                  </span>
                </div>
                {s.evidence ? <Source doc="Constat Bailivo" details={[s.evidence]} /> : <span className="small muted">Aucun élément enregistré dans Bailivo pour cette étape.</span>}
                {s.confirmed_at ? <span className="small muted">Confirmé manuellement le {dateTime(s.confirmed_at)}{s.confirmed_by ? ` par ${s.confirmed_by}` : ""}.</span> : null}
                {s.document ? <DocLink doc={s.document} /> : null}
                {editable ? (
                  <div className="entree-grid-form">
                    <Select label="État" value={v.status} options={STEP_STATUSES} onChange={(e) => setSteps({ ...steps, [s.code]: { ...v, status: e.target.value } })} />
                    <Input label="Précision" value={v.note} error={fieldError(error, `steps.${s.code}.note`)} onChange={(e) => setSteps({ ...steps, [s.code]: { ...v, note: e.target.value } })} />
                    <FileDrop
                      label="Joindre un justificatif"
                      busy={busy === `doc-${s.code}`}
                      onFiles={(files) =>
                        run(`doc-${s.code}`, async () => {
                          const fd = new FormData();
                          fd.append("step", s.code);
                          fd.append("file", files[0]);
                          const r = await upload<{ document: { id: string } }>(`${base}/handover/documents`, fd);
                          setSteps({ ...steps, [s.code]: { ...v, document_id: r.document.id } });
                        }, "Justificatif déposé : enregistrez la liste pour le rattacher.")
                      }
                    />
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </Card>
      <Card title="Clés et badges remis">
        {editable ? (
          <div className="stack">
            {keyRows.map((k, i) => (
              <div key={i} className="entree-grid-form">
                <Input label="Désignation" value={k.label} onChange={(e) => setKeys(keyRows.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
                <Input label="Nombre" type="number" min={0} value={String(k.count)} onChange={(e) => setKeys(keyRows.map((x, j) => (j === i ? { ...x, count: Number(e.target.value) || 0 } : x)))} />
                <Button variant="discret" small onClick={() => setKeys(keyRows.filter((_, j) => j !== i))}>
                  Retirer
                </Button>
              </div>
            ))}
            <div className="entree-actions">
              <Button variant="secondaire" small onClick={() => setKeys([...keyRows, { label: "", count: 1 }])}>
                Ajouter une ligne
              </Button>
              {h.keys_from_inspection.length ? (
                <Button variant="discret" small onClick={() => setKeys(h.keys_from_inspection.map((k) => ({ label: k.label, count: k.count })))}>
                  Reprendre les clés de l’état des lieux
                </Button>
              ) : null}
            </div>
            <Input label="Date réelle de remise des clés" type="date" value={day ?? h.handed_over_on ?? ""} onChange={(e) => setDay(e.target.value)} />
            <div className="entree-actions">
              <Button
                busy={busy === "save"}
                onClick={() =>
                  run(
                    "save",
                    async () => {
                      await put(`${base}/handover`, { steps, keys: keyRows, handed_over_on: day ?? h.handed_over_on });
                      setSteps({});
                      setKeys(null);
                      setDay(null);
                      refresh();
                    },
                    "Remise des clés enregistrée.",
                  )
                }
              >
                Enregistrer
              </Button>
              <Button variant="secondaire" busy={busy === "att"} onClick={() => run("att", async () => { await post(`${base}/handover/attestation`); refresh(); }, "Attestation établie.")}>
                Établir l’attestation de remise des clés
              </Button>
            </div>
          </div>
        ) : (
          <ul className="list-plain">
            {h.keys.map((k) => (
              <li key={k.label}>
                {k.label} : {k.count}
              </li>
            ))}
          </ul>
        )}
        {h.attestation ? (
          <div className="stack-sm">
            <DocLink doc={h.attestation} label="Attestation de remise des clés" />
            <span className="small">
              Bailleur : {h.landlord_confirmed_at ? `confirmé le ${dateTime(h.landlord_confirmed_at)}` : "à confirmer"} · Locataire :{" "}
              {h.tenant_confirmed_at ? `réception confirmée le ${dateTime(h.tenant_confirmed_at)}` : "confirmation attendue depuis son espace"}
            </span>
          </div>
        ) : null}
      </Card>
      {d.lease.status === "actif" ? null : <p className="small muted">Pensez à activer la location (onglet « Bail et annexes ») pour ouvrir l’espace des locataires.</p>}
    </div>
  );
}
